/* eslint-disable react/no-unknown-property */
import { useFrame } from "@react-three/fiber";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { CatmullRomCurve3, Group, Object3D, Vector3 } from "three";

const JOINT_PROXIMITY_THRESHOLD = 0.001;

interface SkeletonProps {
  modelObject: Object3D | null;
}

const getBonePosition = (bone: Object3D): Vector3 => {
  return new Vector3().setFromMatrixPosition(bone.matrixWorld);
};

// calculate average bone length to determine appropriate sizing
const calculateModelScale = (bones: Object3D[]): number => {
  if (bones.length < 2) return 1;

  let totalLength = 0;
  let count = 0;

  bones.forEach((bone) => {
    const parent = bone.parent;
    if (parent && parent.type === "Bone") {
      const bonePos = getBonePosition(bone);
      const parentPos = getBonePosition(parent);
      const length = bonePos.distanceTo(parentPos);
      if (length > JOINT_PROXIMITY_THRESHOLD) {
        totalLength += length;
        count++;
      }
    }
  });

  const avgBoneLength = count > 0 ? totalLength / count : 1;
  return avgBoneLength * 0.1; // 10% of average bone length for joint size
};

export const Skeleton = (props: SkeletonProps) => {
  const { modelObject } = props;
  const skeletonRef = useRef<Group>(null);
  const bonesRef = useRef<Object3D[]>([]);
  const jointMeshesRef = useRef<THREE.Mesh[]>([]);
  const boneMeshesRef = useRef<
    { mesh: THREE.Mesh; startBone: Object3D; endBone: Object3D }[]
  >([]);
  const modelScaleRef = useRef<number>(1);
  const [isInitialized, setIsInitialized] = useState(false);

  // Find all bones once when model loads
  useEffect(() => {
    if (!modelObject) {
      bonesRef.current = [];
      jointMeshesRef.current = [];
      boneMeshesRef.current = [];
      setIsInitialized(false);
      return;
    }

    setIsInitialized(false);

    // force matrix update before finding bones
    modelObject.updateMatrixWorld(true);

    const foundBones: Object3D[] = [];

    modelObject.traverse((child) => {
      if (child.type === "Bone") {
        foundBones.push(child);
      }
    });

    bonesRef.current = foundBones;
    jointMeshesRef.current = [];
    boneMeshesRef.current = [];

    // Calculate scale for skeleton visualization
    if (foundBones.length > 0) {
      modelScaleRef.current = calculateModelScale(foundBones);
    }
  }, [modelObject, calculateModelScale]);

  // update bone/joint positions every frame
  useFrame(() => {
    if (!modelObject || bonesRef.current.length === 0) return;

    const scale = modelScaleRef.current;

    jointMeshesRef.current.forEach((mesh, i) => {
      if (mesh && bonesRef.current[i]) {
        const pos = getBonePosition(bonesRef.current[i]);
        mesh.position.copy(pos);
        mesh.scale.setScalar(scale);
      }
    });

    // update bone tube geometries
    boneMeshesRef.current.forEach(({ mesh, startBone, endBone }) => {
      if (mesh && startBone && endBone) {
        const start = getBonePosition(startBone);
        const end = getBonePosition(endBone);

        // skip if positions are too close
        if (start.distanceTo(end) < JOINT_PROXIMITY_THRESHOLD) {
          mesh.visible = false;
          return;
        }

        mesh.visible = true;
        const curve = new CatmullRomCurve3([start, end]);
        const tubeRadius = scale * 0.5; // Half of joint size
        const geometry = new THREE.TubeGeometry(curve, 1, tubeRadius, 8, false);
        mesh.geometry.dispose();
        mesh.geometry = geometry;
      }
    });

    // Mark as initialized after first frame update
    if (!isInitialized) {
      setIsInitialized(true);
    }
  });

  if (!modelObject || bonesRef.current.length === 0 || !isInitialized) {
    return null;
  }

  const bones = bonesRef.current;
  const scale = modelScaleRef.current;

  boneMeshesRef.current = [];

  return (
    // joints
    <group ref={skeletonRef}>
      {bones.map((bone, i) => {
        const position = getBonePosition(bone);
        return (
          <mesh
            key={`joint-${bone.name}-${i}`}
            position={position}
            scale={scale}
            renderOrder={999}
            ref={(mesh) => {
              if (mesh) {
                jointMeshesRef.current[i] = mesh;
              }
            }}
          >
            <sphereGeometry args={[1, 10, 10]} />
            <meshStandardMaterial color={0x0000ff} depthTest={false} />
          </mesh>
        );
      })}

      {/* bones */}
      {bones.map((bone, i) => {
        const parent = bone.parent;
        if (!parent || parent.type !== "Bone") {
          return null;
        }

        const parentBone = bones.find((b) => b === parent);
        if (!parentBone) {
          return null;
        }

        const start = getBonePosition(parentBone);
        const end = getBonePosition(bone);

        // skip degenerate bones
        if (start.distanceTo(end) < JOINT_PROXIMITY_THRESHOLD) {
          return null;
        }

        const tubeRadius = scale * 0.5; // half of joint size

        return (
          <mesh
            key={`bone-${bone.name}-${i}`}
            renderOrder={999}
            ref={(mesh) => {
              if (mesh) {
                boneMeshesRef.current.push({
                  mesh,
                  startBone: parentBone,
                  endBone: bone,
                });
              }
            }}
          >
            <tubeGeometry
              args={[
                new CatmullRomCurve3([start, end]),
                1, // path segments
                tubeRadius,
                8, // radial segments
                false, // closed
              ]}
            />
            <meshStandardMaterial color="#A48CFD" depthTest={false} />
          </mesh>
        );
      })}
    </group>
  );
};
